// ABOUTME: Manages shopping wishlist, price alerts, and browsing history
// ABOUTME: Persists shopping data to localStorage using React Context

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { Product, WishlistItem } from '@/types/models'

export interface ShoppingState {
  wishlist: WishlistItem[]
  recentlyViewed: Product[]
  priceAlerts: Array<{
    productId: string
    targetPrice: number
    currentPrice: number
    notified: boolean
  }>
}

interface ShoppingActions {
  addToWishlist: (product: Product, targetPrice?: number) => void
  removeFromWishlist: (productId: string) => void
  isInWishlist: (productId: string) => boolean
  updateWishlistItem: (productId: string, updates: Partial<WishlistItem>) => void
  addToRecentlyViewed: (product: Product) => void
  clearRecentlyViewed: () => void
  setPriceAlert: (productId: string, targetPrice: number, currentPrice: number) => void
  removePriceAlert: (productId: string) => void
  checkPriceAlerts: (products: Product[]) => Product[]
  clearAll: () => void
}

type ShoppingContextValue = ShoppingState & ShoppingActions

const STORAGE_KEY = 'gentleman-shopping-store'
const MAX_RECENTLY_VIEWED = 10

const initialState: ShoppingState = {
  wishlist: [],
  recentlyViewed: [],
  priceAlerts: [],
}

function loadPersistedState(): ShoppingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        wishlist: parsed.wishlist ?? [],
        recentlyViewed: parsed.recentlyViewed ?? [],
        priceAlerts: parsed.priceAlerts ?? [],
      }
    }
  } catch {}
  return initialState
}

function persistState(state: ShoppingState): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        wishlist: state.wishlist,
        recentlyViewed: state.recentlyViewed,
        priceAlerts: state.priceAlerts,
      })
    )
  } catch {}
}

const ShoppingContext = createContext<ShoppingContextValue | null>(null)

export function ShoppingStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ShoppingState>(loadPersistedState)

  const update = useCallback((updater: (prev: ShoppingState) => ShoppingState) => {
    setState((prev) => {
      const next = updater(prev)
      persistState(next)
      return next
    })
  }, [])

  const addToWishlist = useCallback(
    (product: Product, targetPrice?: number) => {
      update((s) => {
        if (s.wishlist.find((w) => w.productId === product.id)) return s
        const priceWhenAdded =
          product.onSale && product.salePrice ? product.salePrice : product.price
        const newItem: WishlistItem = {
          id: crypto.randomUUID(),
          productId: product.id,
          product,
          addedAt: new Date(),
          priceWhenAdded,
          targetPrice,
        }
        const next: ShoppingState = { ...s, wishlist: [...s.wishlist, newItem] }
        if (targetPrice) {
          const existing = next.priceAlerts.find((a) => a.productId === product.id)
          if (existing) {
            next.priceAlerts = next.priceAlerts.map((a) =>
              a.productId === product.id ? { ...a, targetPrice, currentPrice: priceWhenAdded } : a
            )
          } else {
            next.priceAlerts = [
              ...next.priceAlerts,
              { productId: product.id, targetPrice, currentPrice: priceWhenAdded, notified: false },
            ]
          }
        }
        return next
      })
    },
    [update]
  )

  const removeFromWishlist = useCallback(
    (productId: string) => {
      update((s) => ({
        ...s,
        wishlist: s.wishlist.filter((w) => w.productId !== productId),
        priceAlerts: s.priceAlerts.filter((a) => a.productId !== productId),
      }))
    },
    [update]
  )

  const isInWishlist = useCallback(
    (productId: string) => state.wishlist.some((w) => w.productId === productId),
    [state.wishlist]
  )

  const updateWishlistItem = useCallback(
    (productId: string, updates: Partial<WishlistItem>) => {
      update((s) => ({
        ...s,
        wishlist: s.wishlist.map((item) =>
          item.productId === productId ? { ...item, ...updates } : item
        ),
      }))
    },
    [update]
  )

  const addToRecentlyViewed = useCallback(
    (product: Product) => {
      update((s) => {
        const filtered = s.recentlyViewed.filter((p) => p.id !== product.id)
        return { ...s, recentlyViewed: [product, ...filtered].slice(0, MAX_RECENTLY_VIEWED) }
      })
    },
    [update]
  )

  const clearRecentlyViewed = useCallback(() => {
    update((s) => ({ ...s, recentlyViewed: [] }))
  }, [update])

  const setPriceAlert = useCallback(
    (productId: string, targetPrice: number, currentPrice: number) => {
      update((s) => {
        const existing = s.priceAlerts.find((a) => a.productId === productId)
        if (existing) {
          return {
            ...s,
            priceAlerts: s.priceAlerts.map((a) =>
              a.productId === productId ? { ...a, targetPrice, currentPrice } : a
            ),
          }
        }
        return {
          ...s,
          priceAlerts: [
            ...s.priceAlerts,
            { productId, targetPrice, currentPrice, notified: false },
          ],
        }
      })
    },
    [update]
  )

  const removePriceAlert = useCallback(
    (productId: string) => {
      update((s) => ({
        ...s,
        priceAlerts: s.priceAlerts.filter((a) => a.productId !== productId),
      }))
    },
    [update]
  )

  const checkPriceAlerts = useCallback(
    (products: Product[]) => {
      const triggered: Product[] = []
      update((s) => {
        let alerts = s.priceAlerts
        for (const alert of alerts) {
          if (alert.notified) continue
          const product = products.find((p) => p.id === alert.productId)
          if (!product) continue
          const currentPrice =
            product.onSale && product.salePrice ? product.salePrice : product.price
          if (currentPrice <= alert.targetPrice) {
            triggered.push(product)
            alerts = alerts.map((a) =>
              a.productId === alert.productId ? { ...a, notified: true, currentPrice } : a
            )
          }
        }
        return { ...s, priceAlerts: alerts }
      })
      return triggered
    },
    [update]
  )

  const clearAll = useCallback(() => {
    setState(initialState)
    persistState(initialState)
  }, [])

  const value = useMemo<ShoppingContextValue>(
    () => ({
      ...state,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      updateWishlistItem,
      addToRecentlyViewed,
      clearRecentlyViewed,
      setPriceAlert,
      removePriceAlert,
      checkPriceAlerts,
      clearAll,
    }),
    [
      state,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      updateWishlistItem,
      addToRecentlyViewed,
      clearRecentlyViewed,
      setPriceAlert,
      removePriceAlert,
      checkPriceAlerts,
      clearAll,
    ]
  )

  return <ShoppingContext.Provider value={value}>{children}</ShoppingContext.Provider>
}

export function useShoppingStore(): ShoppingContextValue {
  const ctx = useContext(ShoppingContext)
  if (!ctx) throw new Error('useShoppingStore must be used within ShoppingStoreProvider')
  return ctx
}
